import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Eye, EyeOff, Tag, CalendarDays, Users, ImagePlus } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const empty = { kind: "offer", title: "", description: "", date: "", price: "", seats: "" };

export const ProgramsManager = () => {
  const [programs, setPrograms] = useState([]);
  const [regs, setRegs] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/admin/programs"), api.get("/registrations")]);
      setPrograms(p.data); setRegs(r.data);
      try { const pu = await api.get("/packages/purchases"); setPurchases(pu.data); } catch { /* ignore */ }
    } catch { toast.error("Failed to load programmes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (form.title.trim().length < 2) return toast.error("Please enter a title");
    if (!file) return toast.error("Please choose an image");
    const fd = new FormData();
    fd.append("kind", form.kind);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("date", form.date);
    fd.append("price", String(parseInt(form.price || "0", 10) || 0));
    fd.append("seats", String(parseInt(form.seats || "0", 10) || 0));
    fd.append("image", file);
    setSaving(true);
    try {
      await api.post("/programs", fd);
      toast.success("Published!");
      setForm(empty); setFile(null); setFileKey((k) => k + 1);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (p) => {
    try { await api.patch(`/programs/${p.id}`, { is_active: !p.is_active }); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/programs/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-12 gap-6">
      {/* Upload form */}
      <div className="lg:col-span-5">
        <div className="rounded-2xl bg-white border border-[#C9DFAC] p-6">
          <div className="flex items-center gap-2 text-[#20301A]"><ImagePlus size={18} className="text-[#D98BA0]" /><h3 className="font-serif text-xl">Add offer / workshop</h3></div>
          <div className="mt-4 space-y-3">
            <div className="inline-flex rounded-full border border-[#C9DFAC] bg-[#F2F8E8] p-1 w-full">
              {["offer", "workshop"].map((k) => (
                <button key={k} data-testid={`program-kind-${k}`} onClick={() => set("kind", k)}
                  className={`flex-1 rounded-full px-4 py-2 text-xs capitalize transition-all ${form.kind === k ? "bg-[#D98BA0] text-white" : "text-[#55703F]"}`}>{k === "offer" ? "Offer (clients)" : "Workshop (students)"}</button>
              ))}
            </div>
            <input data-testid="program-title-input" placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#D98BA0]" />
            <textarea data-testid="program-desc-input" rows={3} placeholder="Short description" value={form.description} onChange={(e) => set("description", e.target.value)}
              className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#D98BA0] resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="program-date-input" placeholder="Date e.g. 20 Jun 2026" value={form.date} onChange={(e) => set("date", e.target.value)}
                className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#D98BA0]" />
              <input data-testid="program-price-input" type="number" min="0" placeholder="Price ₹ (0 = free)" value={form.price} onChange={(e) => set("price", e.target.value)}
                className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#D98BA0]" />
            </div>
            <input data-testid="program-seats-input" type="number" min="0" placeholder="Seats / spots (0 = unlimited)" value={form.seats} onChange={(e) => set("seats", e.target.value)}
              className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-2.5 text-sm outline-none focus:border-[#D98BA0]" />
            <label data-testid="program-image-label" className="flex items-center gap-3 rounded-xl border border-dashed border-[#D98BA0] bg-[#FBEEF2] px-4 py-3 cursor-pointer text-sm text-[#55703F]">
              <Upload size={16} className="text-[#D98BA0]" />
              <span className="truncate">{file ? file.name : "Choose an image (JPG, PNG, WEBP · max 8 MB)"}</span>
              <input data-testid="program-image-input" key={fileKey} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button data-testid="program-publish-button" onClick={create} disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D98BA0] text-white px-6 py-3 text-sm hover:bg-[#c2748c] transition-colors disabled:opacity-60">
              {saving ? <><Loader2 size={16} className="animate-spin" /> Publishing…</> : "Publish"}
            </button>
          </div>
        </div>
      </div>

      {/* Lists */}
      <div className="lg:col-span-7">
        {loading ? (
          <div className="py-20 grid place-items-center text-[#55703F]"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <h3 className="font-serif text-xl text-[#20301A] mb-3">Published ({programs.length})</h3>
            {programs.length === 0 ? (
              <div className="py-10 text-center text-[#55703F] rounded-2xl border border-dashed border-[#C9DFAC] bg-[#DFF0C4]/40">No offers or workshops yet — add one on the left.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-programs-list">
                {programs.map((p) => (
                  <div key={p.id} data-testid={`admin-program-${p.id}`} className="rounded-2xl bg-white border border-[#C9DFAC] overflow-hidden">
                    <div className="relative aspect-[16/9]">
                      <img src={`${BACKEND}${p.image_url}`} alt={p.title} className="w-full h-full object-cover" />
                      <span className={`absolute top-2 left-2 rounded-full px-2.5 py-0.5 text-[0.6rem] uppercase tracking-wider font-mono ${p.kind === "offer" ? "bg-[#D98BA0] text-white" : "bg-[#3A7D32] text-[#F2F8E8]"}`}>{p.kind}</span>
                      {!p.is_active && <span className="absolute top-2 right-2 rounded-full bg-black/60 text-white px-2.5 py-0.5 text-[0.6rem] uppercase tracking-wider">Hidden</span>}
                    </div>
                    <div className="p-4">
                      <div className="font-serif text-lg text-[#20301A] leading-tight">{p.title}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-[#55703F]">
                        {p.date && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> {p.date}</span>}
                        <span className="inline-flex items-center gap-1"><Tag size={11} /> {p.price > 0 ? `₹${p.price / 100}` : "Free"}</span>
                        <span className="inline-flex items-center gap-1"><Users size={11} /> {p.registered} registered{p.seats > 0 ? ` / ${p.seats}` : ""}{p.full ? " · full" : ""}</span>
                        {p.waitlist > 0 && <span className="inline-flex items-center gap-1 text-[#C2748C]">+{p.waitlist} waitlist</span>}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button data-testid={`program-toggle-${p.id}`} onClick={() => toggle(p)} className="inline-flex items-center gap-1 rounded-full border border-[#C9DFAC] text-[#3A7D32] px-3 py-1.5 text-xs hover:bg-[#DFF0C4]">
                          {p.is_active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                        </button>
                        <button data-testid={`program-delete-${p.id}`} onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-full border border-[#FFC9A0] text-[#B0500F] px-3 py-1.5 text-xs hover:bg-[#FFE3C8]"><Trash2 size={12} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h3 className="font-serif text-xl text-[#20301A] mt-8 mb-3 flex items-center gap-2"><Users size={18} className="text-[#D98BA0]" /> Registrations ({regs.length})</h3>
            {regs.length === 0 ? (
              <div className="py-8 text-center text-[#55703F] rounded-2xl border border-dashed border-[#C9DFAC] bg-[#DFF0C4]/40">No registrations yet.</div>
            ) : (
              <div className="grid gap-2" data-testid="admin-registrations-list">
                {regs.map((r) => (
                  <div key={r.id} data-testid={`registration-${r.id}`} className="rounded-xl bg-white border border-[#C9DFAC] p-4 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-[#20301A]">{r.name} <span className="text-xs font-normal text-[#55703F]">· {r.program_title}</span></div>
                      <div className="text-xs text-[#55703F] mt-0.5">{r.email} · {r.phone}{r.note ? ` · ${r.note}` : ""}</div>
                    </div>
                    <span className={`text-[0.65rem] px-2.5 py-1 rounded-full border font-mono uppercase tracking-wider ${r.payment_status === "paid" ? "bg-[#DFF0C4] text-[#3A7D32] border-[#C9DFAC]" : r.payment_status === "waitlist" ? "bg-[#FBEEF2] text-[#C2748C] border-[#EBD4DC]" : r.payment_status === "free" ? "bg-[#D8E5ED] text-[#245a78] border-[#B8D1E0]" : "bg-[#F7EBC6] text-[#7a5f14] border-[#EADB9B]"}`}>{r.payment_status === "free" ? "registered" : r.payment_status}</span>
                  </div>
                ))}
              </div>
            )}

            <h3 className="font-serif text-xl text-[#20301A] mt-8 mb-3 flex items-center gap-2"><Tag size={18} className="text-[#D98BA0]" /> Package purchases ({purchases.length})</h3>
            {purchases.length === 0 ? (
              <div className="py-8 text-center text-[#55703F] rounded-2xl border border-dashed border-[#C9DFAC] bg-[#DFF0C4]/40">No package purchases yet.</div>
            ) : (
              <div className="grid gap-2" data-testid="admin-purchases-list">
                {purchases.map((pu) => (
                  <div key={pu.id} data-testid={`purchase-${pu.id}`} className="rounded-xl bg-white border border-[#C9DFAC] p-4 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-medium text-[#20301A]">{pu.name} <span className="text-xs font-normal text-[#55703F]">· {pu.package_name}</span></div>
                      <div className="text-xs text-[#55703F] mt-0.5">{pu.email} · {pu.phone} · ₹{pu.amount / 100}{pu.note ? ` · ${pu.note}` : ""}</div>
                    </div>
                    <span className={`text-[0.65rem] px-2.5 py-1 rounded-full border font-mono uppercase tracking-wider ${pu.payment_status === "paid" ? "bg-[#DFF0C4] text-[#3A7D32] border-[#C9DFAC]" : "bg-[#F7EBC6] text-[#7a5f14] border-[#EADB9B]"}`}>{pu.payment_status}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
